import { useState, useEffect, useRef } from 'react';
import type { PlaybackStateResponse } from '../types';

/**
 * Options for the progress interpolation hook.
 */
export interface UseProgressInterpolationOptions {
  /**
   * Current playback state from server.
   */
  state: PlaybackStateResponse | null | undefined;

  /**
   * Whether interpolation is enabled.
   * @default true
   */
  enabled?: boolean;

  /**
   * Interval in milliseconds for checking server state changes.
   * Not used for interpolation (which uses requestAnimationFrame).
   * @default 1000
   */
  updateInterval?: number;
}

/**
 * Custom hook that provides smooth client-side progress interpolation
 * for playback progress between server state updates.
 *
 * Uses requestAnimationFrame for smooth 60fps updates when playing.
 * Calculates elapsed time since last server update and adds to server progress.
 *
 * @param options - Configuration options
 * @returns Interpolated progress in milliseconds
 *
 * @example
 * ```tsx
 * const { data: state } = usePlaybackState();
 * const progress = useProgressInterpolation({ state });
 *
 * // Progress smoothly updates at 60fps when playing
 * // Stops interpolation when paused
 * // Syncs immediately with server updates
 * ```
 */
export function useProgressInterpolation(
  options: UseProgressInterpolationOptions
): number {
  const { state, enabled = true } = options;

  // Interpolated progress (ms)
  const [interpolatedProgress, setInterpolatedProgress] = useState<number>(0);

  // Refs for tracking server state
  const animationFrameRef = useRef<number | null>(null);
  const serverProgressRef = useRef<number>(0);
  const serverTimestampRef = useRef<number>(Date.now());

  // Sync with server state updates
  useEffect(() => {
    if (!enabled || !state) {
      setInterpolatedProgress(0);
      serverProgressRef.current = 0;
      serverTimestampRef.current = Date.now();
      return;
    }

    // Update refs with new server state
    const serverProgress = state.progressMs ?? 0;
    serverProgressRef.current = serverProgress;
    serverTimestampRef.current = Date.now();

    // Immediately sync displayed progress with server
    setInterpolatedProgress(serverProgress);
  }, [enabled, state]);

  // Interpolation loop
  useEffect(() => {
    if (!enabled || !state?.isPlaying) {
      // Cancel animation when not playing
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    // Start interpolation loop
    const interpolate = () => {
      const now = Date.now();
      const elapsedMs = now - serverTimestampRef.current;
      const newProgress = serverProgressRef.current + elapsedMs;

      // Clamp to track duration (don't exceed)
      const maxProgress = state.item?.durationMs ?? Infinity;
      const clampedProgress = Math.min(newProgress, maxProgress);

      setInterpolatedProgress(clampedProgress);

      // Continue loop
      animationFrameRef.current = requestAnimationFrame(interpolate);
    };

    // Start first frame
    animationFrameRef.current = requestAnimationFrame(interpolate);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [enabled, state?.isPlaying, state?.item?.durationMs]);

  return interpolatedProgress;
}
